import { useCallback, useEffect, useRef, useState, memo } from 'react';
import PropTypes from 'prop-types';
import { Navigate, useParams } from 'react-router-dom';
import { Typography, makeStyles } from '@material-ui/core';
import PageContainer from '../../shared/pageContainer';
import VirtualizedList from '../../shared/virtualizedList';
import AnnouncementCard from '../../shared/announcementCard';
import CustomButton from '../../shared/customButton';
import CustomFormDrawer from '../../shared/customFormDrawer';
import NotificationCard from '../../shared/notificationCard';
import AlertDialog from '../../shared/alertDialog';
import {
  APP_PAGES,
  ANNOUNCEMENTS_DEFAULT_PARAMS,
  PARAMS,
} from '../../../constants/pages';
import { PERMISSION_CODES } from '../../../constants/permissions';
import {
  ANNOUNCEMENT_CATEGORY,
  ANNOUNCEMENT_CATEGORY_TYPES,
} from '../../../constants/announcements';
import {
  API_ANNOUNCEMENTS,
  api_announcement,
  api_announcements_by_category,
  api_announcement_publish,
  api_announcement_unpublish,
} from '../../../constants/apiRoutes';
import { sticky } from '../../../constants/helperCssRules';
import { hasAuthPermissionCheck } from '../../../utility/permissions';
import {
  prepareAnnouncementFormFields,
  prepareAnnouncementFormInitialData,
  prepareAnnouncementCreateFormData,
  prepareAnnouncementPatchFormData,
  isValidAnnouncementCategory,
} from '../../../utility/announcements';
import { useTranslations } from '../../../utility/useTranslations';
import http from '../../../utility/http';
import { isArrayEmpty } from '../../../utility/helpers';
import { showSuccessMessage, hasNextPage } from '../../../utility/uiUtils';

const CARD_GAP = 16;

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  titleRow: {
    alignItems: 'center',
    display: 'flex',
  },
  iconWrapper: props => ({
    alignItems: 'center',
    backgroundColor: props.backgroundColor,
    borderRadius: 8,
    display: 'flex',
    flexShrink: 0,
    height: 40,
    justifyContent: 'center',
    marginRight: spacing(3),
    width: 40,
    '& path': {
      fill: props.color,
    },
  }),
  icon: {
    height: 20,
    width: 20,
  },
  headerWrapper: {
    maxWidth: 'calc(100% - 260px)',
  },
  descriptionSection: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    gap: spacing(4),
    ...sticky(primary.white, 105),
    padding: spacing(8, 0, 6, 0),
  },
  descriptionText: {
    flex: 1,
  },
  addButton: {
    flexShrink: 0,
  },
  list: {
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
  },
  noResults: {
    marginTop: spacing(6),
  },
  announcementForm: {
    display: 'grid',
    gridTemplateColumns: 'repeat(1, minmax(0, 1fr))',
    gap: spacing(4),
  },
  description: {
    '& .ql-container': {
      height: 320,
    },
    '& > div': {
      height: 389,
    },
  },
  switchLabel: {
    lineHeight: '20px',
    fontSize: 14,
  },
}));

const AnnouncementCategoryPage = ({ navigate, accessControl, ...rest }) => {
  const { categoryKey } = useParams();
  const pageScrollRef = useRef(null);
  const translations = useTranslations(APP_PAGES.ANNOUNCEMENT_CATEGORY);
  const { page: defaultPage } = ANNOUNCEMENTS_DEFAULT_PARAMS;

  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [announcements, setAnnouncements] = useState({
    results: [],
    next: null,
    count: 0,
  });
  const [currentPage, setCurrentPage] = useState(defaultPage);
  const [isNextPageLoading, setIsNextPageLoading] = useState(false);
  const [announcementToDelete, setAnnouncementToDelete] = useState(null);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [isAnnouncementDrawerOpened, setIsAnnouncementDrawerOpened] =
    useState(false);

  const isCategoryValid = isValidAnnouncementCategory(categoryKey);
  const safeCategoryKey = isCategoryValid
    ? categoryKey.toUpperCase()
    : ANNOUNCEMENT_CATEGORY.LEGAL;
  const categoryConfig = ANNOUNCEMENT_CATEGORY_TYPES[safeCategoryKey];
  const categoryTranslations = translations.categories[safeCategoryKey];
  const { color, backgroundColor, icon: CategoryIcon } = categoryConfig;
  const classes = useStyles({ color, backgroundColor });
  const isEditAnnouncement = !!selectedAnnouncement?.id;
  const announcementFormInitialData = prepareAnnouncementFormInitialData(
    safeCategoryKey,
    selectedAnnouncement
  );
  const announcementFields = prepareAnnouncementFormFields(classes);
  const hasLoadMore = hasNextPage(announcements);
  const canManage = hasAuthPermissionCheck(
    PERMISSION_CODES.ANNOUNCEMENTS_MANAGE,
    accessControl
  );

  const handlePageChange = useCallback(
    async ({ newPage = defaultPage, isLoadMore = false } = {}) => {
      const { data } = await http.get(
        api_announcements_by_category(safeCategoryKey),
        {
          params: { ...ANNOUNCEMENTS_DEFAULT_PARAMS, [PARAMS.PAGE]: newPage },
        }
      );
      setAnnouncements(prev =>
        isLoadMore
          ? {
              ...(data ?? {}),
              results: [...(prev.results ?? []), ...(data?.results ?? [])],
            }
          : data ?? {}
      );
    },
    [safeCategoryKey, defaultPage]
  );

  const getInitialData = async () => {
    try {
      await handlePageChange({ newPage: defaultPage });
    } finally {
      setIsInitialLoad(false);
    }
  };

  useEffect(() => {
    if (!isCategoryValid) {
      setIsInitialLoad(false);
      return undefined;
    }

    getInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onLoadMore = useCallback(async () => {
    if (isNextPageLoading || !hasLoadMore) return;

    try {
      setIsNextPageLoading(true);
      setCurrentPage(currentPage + 1);
      await handlePageChange({ newPage: currentPage + 1, isLoadMore: true });
    } finally {
      setIsNextPageLoading(false);
    }
  }, [currentPage, hasLoadMore, isNextPageLoading, handlePageChange]);

  const prepareVirtualItemSize = useCallback(() => 180, []);

  const handleOpenCreateDrawer = useCallback(() => {
    setSelectedAnnouncement(null);
    setIsAnnouncementDrawerOpened(true);
  }, []);

  const handleEditAnnouncement = useCallback(announcement => {
    setSelectedAnnouncement(announcement);
    setIsAnnouncementDrawerOpened(true);
  }, []);

  const handleCloseAnnouncementDrawer = useCallback(() => {
    setSelectedAnnouncement(null);
    setIsAnnouncementDrawerOpened(false);
  }, []);

  const onSaveAnnouncement = useCallback(
    async values => {
      const isEdit = !!values.id;

      if (isEdit) {
        const formData = prepareAnnouncementPatchFormData(
          values,
          selectedAnnouncement
        );

        if (!formData) {
          return Promise.resolve();
        }

        await http.patch(api_announcement(values.id), formData);
        showSuccessMessage(translations.announcementForm.editSuccess);
      } else {
        const formData = prepareAnnouncementCreateFormData(values);
        await http.post(API_ANNOUNCEMENTS, formData);
        showSuccessMessage(translations.announcementForm.addSuccess);
      }

      setCurrentPage(defaultPage);
      return handlePageChange({ newPage: defaultPage });
    },
    [
      defaultPage,
      handlePageChange,
      selectedAnnouncement,
      translations.announcementForm,
    ]
  );

  const onPublishAnnouncement = useCallback(
    async announcement => {
      await http.post(api_announcement_publish(announcement.id));
      showSuccessMessage(translations.announcementForm.publishSuccess);
      setCurrentPage(defaultPage);
      await handlePageChange({ newPage: defaultPage });
    },
    [defaultPage, handlePageChange, translations.announcementForm]
  );

  const onUnpublishAnnouncement = useCallback(
    async announcement => {
      await http.post(api_announcement_unpublish(announcement.id));
      showSuccessMessage(translations.announcementForm.unpublishSuccess);
      setCurrentPage(defaultPage);
      await handlePageChange({ newPage: defaultPage });
    },
    [defaultPage, handlePageChange, translations.announcementForm]
  );

  const handleDeleteAnnouncement = useCallback(announcement => {
    setAnnouncementToDelete(announcement);
  }, []);

  const onCloseDeleteDialog = useCallback(() => {
    setAnnouncementToDelete(null);
  }, []);

  const onDeleteAnnouncement = useCallback(async () => {
    if (!announcementToDelete) return;

    try {
      await http.delete(api_announcement(announcementToDelete.id));
      showSuccessMessage(translations.deleteAnnouncementDialog.deleteSuccess);
      setCurrentPage(defaultPage);
      await handlePageChange({ newPage: defaultPage });
    } finally {
      setAnnouncementToDelete(null);
    }
  }, [
    announcementToDelete,
    defaultPage,
    handlePageChange,
    translations.deleteAnnouncementDialog.deleteSuccess,
  ]);

  const renderAnnouncement = useCallback(
    announcement => (
      <AnnouncementCard
        translations={translations.card}
        announcement={announcement}
        category={categoryConfig}
        canManage={canManage}
        onPublish={onPublishAnnouncement}
        onUnpublish={onUnpublishAnnouncement}
        onEdit={handleEditAnnouncement}
        onDelete={handleDeleteAnnouncement}
      />
    ),
    [
      categoryConfig,
      canManage,
      handleDeleteAnnouncement,
      handleEditAnnouncement,
      onPublishAnnouncement,
      onUnpublishAnnouncement,
      translations.card,
    ]
  );

  const renderCategoryHeader = () => (
    <div className={classes.headerWrapper}>
      <div className={classes.titleRow}>
        <div className={classes.iconWrapper}>
          <CategoryIcon className={classes.icon} />
        </div>
        <Typography variant="h1">
          {`${translations.parentTitle}/${categoryTranslations.title}`}
        </Typography>
      </div>
    </div>
  );

  if (!isCategoryValid) {
    return <Navigate to="/announcements" replace />;
  }

  return (
    <PageContainer
      {...rest}
      accessControl={accessControl}
      navigate={navigate}
      translations={translations}
      shouldPassProps={false}
      pageScrollRef={pageScrollRef}
      isMediumWidthContent
      headerProps={{
        renderTitle: renderCategoryHeader,
      }}
    >
      {!isInitialLoad && (
        <div>
          <div className={classes.descriptionSection}>
            <Typography className={classes.descriptionText} variant="body2">
              {categoryTranslations.description}
            </Typography>
            {canManage && (
              <CustomButton
                className={classes.addButton}
                type="addRoundedNew"
                onClick={handleOpenCreateDrawer}
              >
                {translations.newAnnouncement}
              </CustomButton>
            )}
          </div>
          {!isArrayEmpty(announcements.results) && (
            <VirtualizedList
              className={classes.list}
              scrollElementRef={pageScrollRef}
              items={announcements.results}
              count={announcements.results.length}
              gap={CARD_GAP}
              estimateSize={prepareVirtualItemSize}
              renderItem={renderAnnouncement}
              hasNextPage={hasLoadMore}
              isNextPageLoading={isNextPageLoading}
              onLoadMore={onLoadMore}
              shouldUseWindowScroll
              isDynamicSize
            />
          )}
          <NotificationCard
            className={classes.noResults}
            content={translations.noResults}
            shouldFade={isArrayEmpty(announcements.results)}
            shouldFadeOutInstant
          />
          <AlertDialog
            translations={translations.deleteAnnouncementDialog}
            isOpened={!!announcementToDelete}
            onClose={onCloseDeleteDialog}
            onConfirm={onDeleteAnnouncement}
            isWarning
          />
          <CustomFormDrawer
            customContentClass={classes.announcementForm}
            translations={translations.announcementForm}
            isOpened={isAnnouncementDrawerOpened}
            isInitialValid={isEditAnnouncement}
            initialData={announcementFormInitialData}
            fields={announcementFields}
            onClose={handleCloseAnnouncementDrawer}
            onSave={onSaveAnnouncement}
            isMiddleWidth
            hasCancelButton
          />
        </div>
      )}
    </PageContainer>
  );
};

AnnouncementCategoryPage.propTypes = {
  accessControl: PropTypes.object.isRequired,
  navigate: PropTypes.func.isRequired,
};

export default memo(AnnouncementCategoryPage);
